import React, { useEffect, useState } from "react";
import { Button, Card, Modal, ModalBody } from "reactstrap";
import styles from "./ReportModal.module.scss";
import Text from "components/Text";
import crossImg from "../../assets/images/cross-mark-button.svg";
import Input from "components/Input";
import { reportAiMessage } from "repositories/ai-assistant-repository";
import { handleError, handleSuccess } from "utils";
import Loader from "components/Loader";
import constants from "../../constants";

const CustomReportModal = ({
  t,
  setModalOpen,
  modalOpen,
  selectedResponse,
  aiChatMessageId,
  onReportSuccess,
}) => {
  const [comment, setComment] = useState("");
  const [selectedReportOption, setSelectedReportOption] = useState("");
  const [reportError, setReportError] = useState("");
  const [commentError, setCommentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reportOptions = constants.reportOptions;
  const latestResponse =
    Array.isArray(selectedResponse?.reports) &&
    selectedResponse?.reports.length > 0
      ? selectedResponse?.reports[0]
      : null;
  const locked =
    Array.isArray(selectedResponse?.reports) &&
    selectedResponse?.reports.length > 0;

  useEffect(() => {
    if (latestResponse) {
      setSelectedReportOption(latestResponse?.reportType || "");
      setComment(latestResponse?.commentByUser || "");
    }
  }, [selectedResponse?.reports]);

  const handleCommentChange = (e) => {
    setComment(e.target.value);
    if (commentError) {
      setCommentError("");
    }
  };

  const handleReportOptionChange = (optionId) => {
    setSelectedReportOption(optionId);
    if (reportError) {
      setReportError("");
    }

    if (optionId !== 4) {
      setComment("");
      setCommentError("");
    }
  };

  const validateForm = () => {
    let isValid = true;

    if (!selectedReportOption) {
      setReportError(t("aiAssistant.reportModal.reportRequired"));
      isValid = false;
    }

    if (selectedReportOption === 4 && (!comment || comment.trim() === "")) {
      setCommentError(t("aiAssistant.reportModal.commentRequired"));
      isValid = false;
    }

    return isValid;
  };

  const giveReportToAiResponse = async () => {
    try {
      setIsSubmitting(true);
      const params = {
        AiChatMessageId:
          selectedResponse?.messageId ||
          aiChatMessageId ||
          selectedResponse?.id,
        ReportType: selectedReportOption,
      };

      if (selectedReportOption === 4) params.CommentByUser = comment;

      const result = await reportAiMessage(params);
      handleSuccess(result?.message);

      if (typeof onReportSuccess === "function") {
        onReportSuccess({
          report: reportOptions.find((val) => val?.id == selectedReportOption)
            ?.title,
          comment: selectedReportOption === 4 ? comment : "",
        });
      }

      setModalOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      await giveReportToAiResponse();
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    if (!locked) {
      setSelectedReportOption("");
      setComment("");
      setReportError("");
      setCommentError("");
    }
  };

  const getReportOptionTitle = (optionId) => {
    const option = reportOptions.find((opt) => opt.id === optionId);
    return option ? option.title : "";
  };

  return (
    <Modal
      toggle={handleClose}
      isOpen={modalOpen}
      centered={true}
      className={`modal-dialog-centered modal-width-660 ${styles["report-response-pop-up"]}`}
      modalClassName="custom-modal"
    >
      {isSubmitting && <Loader />}
      <Button
        className={styles["PatientAppointmentDetails-cross-btn"]}
        aria-label="Close"
        type="button"
        onClick={handleClose}
      >
        <img src={crossImg} alt="cross button" />
      </Button>

      <Text size="25px" weight="500" color="#111B45" marginBottom="10px">
        {locked
          ? t("aiAssistant.reportModal.reportedResponse")
          : t("aiAssistant.reportModal.title")}
      </Text>

      {!locked && (
        <Text size="16px" weight="300" color="#535B5F" marginBottom="20px">
          {t("aiAssistant.reportModal.subContent")}
        </Text>
      )}

      <ModalBody>
        {locked ? (
          <div
            className={styles["reported-response-container"]}
            style={{
              backgroundColor: "#F9FCEE",
              padding: "20px",
              borderRadius: "8px",
              marginBottom: "20px",
              marginTop: "20px",
            }}
          >
            <div
              className={`mb-3 pb-3 ${
                latestResponse.commentByUser ? styles["border-bottom-1"] : ""
              }`}
            >
              <Text size="14px" weight="400" color="#6B7280" marginBottom="5px">
                {t("aiAssistant.reportModal.reason")}
              </Text>
              <Text size="14px" weight="600" color="#374151">
                {latestResponse?.reportType}
              </Text>
            </div>

            {latestResponse?.commentByUser && (
              <div>
                <Text
                  size="14px"
                  weight="400"
                  color="#6B7280"
                  marginBottom="5px"
                >
                  {t("aiAssistant.reportModal.comment")}
                </Text>
                <Text size="16px" weight="600" color="#374151" overflowWrap>
                  {latestResponse.commentByUser}
                </Text>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="mb-4">
              <Text
                size="16px"
                weight="600"
                color="#2F3245"
                marginBottom="10px"
              >
                {`${t("aiAssistant.reportModal.reason")}: `}
              </Text>
              <div>
                <ul className={styles["change-list"]}>
                  {reportOptions.map((option, index) => (
                    <li
                      key={option.id}
                      style={{
                        borderBottom:
                          index < reportOptions.length - 1
                            ? "1px solid #E5E7EB"
                            : "none",
                        paddingBottom: "5px",
                      }}
                    >
                      <div className="ch-radio">
                        <label className="mr-5">
                          <input
                            type="radio"
                            name="reportReason"
                            value={option.id}
                            checked={selectedReportOption === option.id}
                            onChange={() => handleReportOptionChange(option.id)}
                            disabled={locked}
                          />
                          <span>{option.title}</span>
                        </label>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              {reportError && (
                <Text size="12px" weight="400" color="#D9534F" marginTop="2px">
                  {reportError}
                </Text>
              )}
            </div>

            {selectedReportOption === 4 && (
              <div>
                <Text
                  size="12px"
                  weight="400"
                  color="#6F7788"
                  marginBottom="5px"
                >
                  {t("aiAssistant.reportModal.addComment")}
                </Text>
                <Input
                  Classes="mb-2"
                  Type="textarea"
                  Placeholder="Enter"
                  Name="reportComment"
                  Value={comment}
                  MaxLength={500}
                  HandleChange={handleCommentChange}
                  rows={3}
                />
                <Text size="11px" weight="400" color="#6F7788">
                  {comment.length}/500 characters
                </Text>
                {commentError && (
                  <Text
                    size="12px"
                    weight="400"
                    color="#D9534F"
                    marginTop="6px"
                  >
                    {commentError}
                  </Text>
                )}
              </div>
            )}
          </>
        )}
      </ModalBody>

      {!locked && (
        <div className={styles["common-appointment-btn-container"]}>
          <Button
            className="button button-round button-shadow mr-md-4 mb-3 w-sm-100"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {t("submit")}
          </Button>
          <Button
            className="button button-round button-border button-dark btn-mobile-link"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t("cancel") || "Cancel"}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default CustomReportModal;
